import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, FileText, Newspaper } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function AuthorArticlesPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentCompanyId, isPlatformOwner, isLoading: companyLoading } = useCompany();

  // Belt-and-braces over RLS: refuse to render the author + articles
  // until we know which company context the caller is in, and scope
  // both queries by company_id for non-platform-owners. RLS would also
  // catch a cross-tenant guess but the explicit filter is cheaper and
  // surfaces the intent at the call site.
  const enabled = !!user && !!id && !companyLoading && (isPlatformOwner || !!currentCompanyId);

  const { data: author } = useQuery({
    queryKey: ["author", id, currentCompanyId, isPlatformOwner],
    queryFn: async () => {
      let query = supabase.from("authors").select("*").eq("id", id!);
      if (!isPlatformOwner && currentCompanyId) {
        query = query.eq("company_id", currentCompanyId);
      }
      const { data, error } = await query.single();
      if (error) throw error;
      return data;
    },
    enabled,
  });

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["author-articles", id, currentCompanyId, isPlatformOwner],
    queryFn: async () => {
      let query = supabase
        .from("articles")
        .select("id, title, status, updated_at, featured_image_url, categories(name)")
        .eq("editorial_author_id", id!)
        .order("updated_at", { ascending: false })
        .limit(100);
      if (!isPlatformOwner && currentCompanyId) {
        query = query.eq("company_id", currentCompanyId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled,
  });

  const initials = (name: string) => name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";

  return (
    <div className="p-6 space-y-6">
      <Link to="/authors">
        <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Authors</Button>
      </Link>

      {author && (
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarFallback className="text-lg font-medium">{initials(author.name)}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{author.name}</h1>
            <p className="text-sm text-muted-foreground">{author.bio || "No biography"}</p>
            <p className="text-xs text-muted-foreground mt-1">{articles.length} article{articles.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded" />)}</div>
      ) : articles.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">No articles linked to this author.</p>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-14">Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Modified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.map((a: any) => (
                <TableRow key={a.id} className="hover:bg-muted/60 transition-colors">
                  <TableCell className="py-2">
                    <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                      {a.featured_image_url ? <img src={a.featured_image_url} alt="" className="h-full w-full object-cover" /> : <Newspaper className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Link to={`/articles/${a.id}`} className="font-medium text-foreground hover:text-primary transition-colors line-clamp-1">{a.title}</Link>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{a.categories?.name || "—"}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[11px]">{a.status}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {(() => { try { return formatDistanceToNow(new Date(a.updated_at), { addSuffix: true }); } catch { return "—"; } })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
